import queryString from "query-string";
import { useCallback } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

import { QueryActionFormInputs } from "../types/form-inputs";
import { ErrorMessage } from "./catalyst/ErrorMessage";
import { Field } from "./catalyst/Field";
import { Label } from "./catalyst/Label";
import EntityAwareIntellisenseEditor from "./json/EntityAwareIntellisenseEditor";
import { Tooltip } from "./Tooltip";

export const QueryActionURLField = ({
  baseUrl,
}: {
  baseUrl: string;
}): JSX.Element => {
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<QueryActionFormInputs>();

  const { replace } = useFieldArray({
    control,
    name: "httpParameters",
  });

  const handlePaste = useCallback(
    (pastedText: string): { replacementText?: string } | false => {
      try {
        const { query: queryParams, url: urlWithoutQuery } =
          queryString.parseUrl(pastedText.trim());

        if (Object.keys(queryParams).length > 0) {
          const existingValues = getValues("httpParameters") ?? [];
          const parsedParameters: Array<{
            description: string;
            name: string;
            type: "QueryParameter";
            value: string;
          }> = [];

          // Convert all parsed query parameters to the parameter format
          Object.entries(queryParams).forEach(([name, value]) => {
            if (value === null || value === undefined) return;

            const stringValue = Array.isArray(value)
              ? value.join(",")
              : String(value);

            parsedParameters.push({
              description: "",
              name,
              type: "QueryParameter",
              value: stringValue,
            });
          });

          // Replace the entire array
          const nonQueryParams = existingValues.filter(
            (p) => p.type !== "QueryParameter",
          );
          replace([...nonQueryParams, ...parsedParameters]);

          const valueToSet = urlWithoutQuery.startsWith(baseUrl)
            ? urlWithoutQuery.replace(baseUrl, "")
            : urlWithoutQuery;

          if (parsedParameters.length > 0) {
            toast.info("Query parameters updated from pasted URL");
          }

          return { replacementText: valueToSet };
        } else if (urlWithoutQuery.startsWith(baseUrl)) {
          return { replacementText: urlWithoutQuery.replace(baseUrl, "") };
        }
      } catch (error) {
        console.error("Failed to parse pasted URL:", error);
      }

      return false;
    },
    [baseUrl, getValues, replace, setValue],
  );

  return (
    <>
      <div className="min-w-0 overflow-hidden sm:col-span-6">
        <Field>
          <Label>URL</Label>

          <div className="mt-3 flex min-w-0 items-center gap-2">
            <div className="relative flex min-w-0 max-w-full flex-1 items-center overflow-hidden rounded-lg border border-gray-600 px-2 text-white">
              <div className="min-w-0 max-w-[44ch] shrink">
                <Tooltip content={baseUrl}>
                  <span className="block truncate text-gray-400">
                    {baseUrl}
                  </span>
                </Tooltip>
              </div>

              <div className="mx-2 h-6 w-[1px] shrink-0 bg-gray-600" />
              <Controller
                control={control}
                defaultValue=""
                name="url"
                render={({ field: { name, onChange, value } }) => (
                  <div className="min-w-0 flex-1">
                    <EntityAwareIntellisenseEditor
                      bordered={false}
                      fieldName={name}
                      noMargin={true}
                      onChange={(value) => {
                        onChange(value || "");
                      }}
                      onPaste={handlePaste}
                      readOnly={false}
                      singleLine={true}
                      value={value}
                    />
                  </div>
                )}
                rules={{
                  validate: (value) => {
                    if (
                      value.includes("?") ||
                      value.includes("&") ||
                      value.includes("=")
                    ) {
                      return "URL must not contain query parameters, use query parameters tab";
                    }
                    return true;
                  },
                }}
              />
            </div>
          </div>

          {errors.url && <ErrorMessage>{errors.url.message}</ErrorMessage>}
        </Field>
      </div>
    </>
  );
};
